import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm mb-8 inline-block">&larr; Back to Home</Link>

        <h1 className="font-serif text-4xl font-semibold text-secondary-800 mb-2">Terms of Service</h1>
        <p className="text-secondary-500 text-sm mb-12">Last updated: February 2026</p>

        <div className="prose prose-lg max-w-none space-y-8">

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">1. Introduction</h2>
            <p className="text-secondary-600 leading-relaxed">
              These Terms of Service ("Terms") govern your use of the website and services provided by The Family Legacy Bridge, operated by Amy Jaffe. By accessing or using our website and services, you agree to be bound by these Terms. If you do not agree, please do not use our services.
            </p>
          </section>

          <section className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">2. Important Disclaimer — Not Financial Advice</h2>
            <p className="text-secondary-700 leading-relaxed font-medium mb-4">
              The Family Legacy Bridge provides family wealth coaching, facilitation, and educational services. We do not provide financial advice, investment advice, tax advice, or any other services that require registration as a Financial Services Provider under the Financial Advisory and Intermediary Services Act, 2002 (FAIS Act) of South Africa.
            </p>
            <p className="text-secondary-700 leading-relaxed mb-4">
              Amy Jaffe is not a registered Financial Services Provider (FSP) with the Financial Sector Conduct Authority (FSCA). Nothing on this website or in our services should be construed as financial, investment, legal, or tax advice.
            </p>
            <p className="text-secondary-700 leading-relaxed">
              We strongly recommend that you consult with appropriately qualified and registered professionals — including financial advisors, tax practitioners, and legal counsel — before making any financial decisions. Our services are intended to complement, not replace, professional financial advice.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">3. Nature of Our Services</h2>
            <p className="text-secondary-600 leading-relaxed mb-4">The Family Legacy Bridge provides the following services:</p>
            <ul className="space-y-2 text-secondary-600">
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Family wealth coaching and facilitation to help families communicate about wealth stewardship.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Family assessment tools to identify alignment, values, and opportunities for growth.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Educational resources on family governance, legacy planning, and intergenerational engagement.</span>
              </li>
            </ul>
            <p className="text-secondary-600 leading-relaxed mt-4">
              These services are coaching and educational in nature and do not constitute regulated financial services under the FAIS Act.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">4. Confidentiality</h2>
            <p className="text-secondary-600 leading-relaxed">
              We treat all information shared during consultations, assessments, and communications as strictly confidential. We will not disclose your personal or family information to any third party without your explicit written consent, except where required by South African law.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">5. User Responsibilities</h2>
            <p className="text-secondary-600 leading-relaxed mb-4">By using our services, you agree to:</p>
            <ul className="space-y-2 text-secondary-600">
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Provide accurate and truthful information in assessments and consultations.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Use our services for lawful purposes only.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Seek independent professional advice for financial, legal, and tax matters.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Not reproduce, distribute, or share assessment content or materials without permission.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">6. Intellectual Property</h2>
            <p className="text-secondary-600 leading-relaxed">
              All content on this website — including text, assessment tools, methodologies, graphics, and design — is the intellectual property of The Family Legacy Bridge. You may not reproduce, modify, distribute, or use any content without our prior written consent.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">7. Limitation of Liability</h2>
            <p className="text-secondary-600 leading-relaxed mb-4">
              To the fullest extent permitted by South African law:
            </p>
            <ul className="space-y-2 text-secondary-600">
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>The Family Legacy Bridge shall not be liable for any direct, indirect, incidental, or consequential damages arising from your use of our services.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>We do not guarantee any specific outcomes from our coaching or assessment services.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Any decisions made based on insights from our services are your sole responsibility.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">8. Consumer Protection</h2>
            <p className="text-secondary-600 leading-relaxed">
              These Terms are subject to the Consumer Protection Act, 2008 (CPA) of South Africa where applicable. Nothing in these Terms is intended to limit or exclude any rights you may have under the CPA.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">9. Governing Law</h2>
            <p className="text-secondary-600 leading-relaxed">
              These Terms are governed by and construed in accordance with the laws of the Republic of South Africa. Any disputes arising from these Terms or your use of our services shall be subject to the jurisdiction of the South African courts.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">10. Changes to These Terms</h2>
            <p className="text-secondary-600 leading-relaxed">
              We may update these Terms from time to time. Any changes will be posted on this page with an updated revision date. Continued use of our services after changes are posted constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">11. Contact</h2>
            <p className="text-secondary-600 leading-relaxed">
              If you have any questions about these Terms, please contact:
            </p>
            <div className="bg-secondary-50 rounded-xl p-6 mt-4">
              <p className="text-secondary-700 font-medium">Amy Jaffe</p>
              <p className="text-secondary-600">The Family Legacy Bridge</p>
              <p className="text-secondary-600">Email: amyjaffe@outlook.com</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
